import { useBreakpoint } from "@amzn/aws-euc-ui";
import { useLayoutEffect } from "react";
import { getLegacyFlag, getMobileDeviceFlag } from "../utils/toolbarItemUtils";
import { SetCookiesFromUrlParamsStatus } from "./useSetCookiesFromUrlParams";
import { isDeviceOnExperimentalMode } from "../utils/userAgentUtils";
import { ColorTheme, getCustomBrandingConfig } from "../configurations";
import {
    EMPTY_LOGO_GIF,
    getCustomBrandingResourceCdnPath,
    initializeCustomBrandingTranslations,
} from "../utils/customBrandingUtils";
import { CustomBrandingCdnResource } from "../types/customBranding";
import wsbFavicon from "../resources/WSB_favicon.ico";
import { checkImageUrlExists } from "../utils/networkUtils";
import { useCustomBrandingAssetStore } from "./useCustomBrandingAsset";
import { applyMode, Mode } from "@cloudscape-design/global-styles";
import log, { METRIC_NAME } from "../logging";
import { ClassName } from "../constants/Styles";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

interface AppInitializationProps {
    settingCookiesFromUrlParamsStatus: SetCookiesFromUrlParamsStatus;
}

export const useAppInitialization = (props: AppInitializationProps) => {
    const { isDesktopOrLaptop } = useBreakpoint();

    useLayoutEffect(() => {
        // TODO For mobile users, present the legacy experience until we can resolve the on-screen keyboard issue. (https://issues.amazon.com/issues/LOWA-11655)
        const isLegacy = getLegacyFlag() || isDeviceOnExperimentalMode();
        const isMobileFlagOn = getMobileDeviceFlag();
        const shouldUseLegacy = isLegacy && !isMobileFlagOn;
        const body = document.getElementsByTagName("body")[0];

        body.className = classNames(body.className, {
            [ClassName.LEGACY_BODY_BACKGROUND_STYLE]: shouldUseLegacy,
            [ClassName.EUC_BODY_BACKGROUND_STYLE]: !shouldUseLegacy,
        });
    }, [props.settingCookiesFromUrlParamsStatus]);

    useLayoutEffect(() => {
        document.documentElement.className = classNames(
            document.documentElement.className,
            {
                [ClassName.DESKTOP_LAPTOP_DEVICE_TYPE]: isDesktopOrLaptop,
                [ClassName.MOBILE_TABLET_DEVICE_TYPE]: !isDesktopOrLaptop,
            }
        );
    }, [isDesktopOrLaptop]);

    useInitializeCustomBranding();
};

const useInitializeCustomBranding = () => {
    const setBrandLogoPath = useCustomBrandingAssetStore(
        (store) => store.setBrandLogoPath
    );
    const setHasTermsOfService = useCustomBrandingAssetStore(
        (store) => store.setHasTermsOfService
    );
    const setIsResourceReady = useCustomBrandingAssetStore(
        (store) => store.setIsResourceReady
    );
    const setCustomFontBasedCloudscapeTheme = useCustomBrandingAssetStore(
        (store) => store.setCustomFontBasedCloudscapeTheme
    );
    const { t } = useTranslation();
    const browserTabTitle = t("branding.browserTabTitle", {
        defaultValue: "WorkSpaces Secure Browser",
    });

    const customBrandingConfig = getCustomBrandingConfig();

    useLayoutEffect(() => {
        document.title = browserTabTitle;
    }, [browserTabTitle]);

    useLayoutEffect(() => {
        const initializeCustomBrandingAsset = async () => {
            const {
                userSettingId,
                wallpaperExtension,
                faviconExtension,
                logoExtension,
                colorTheme,
                isTermsOfServiceProvided,
            } = customBrandingConfig ?? {};

            if (
                customBrandingConfig?.userSettingId &&
                customBrandingConfig.localizedStrings
            ) {
                initializeCustomBrandingTranslations();
            }

            // Apply custom background class when custom branding is configured
            // regardless of whether wallpaper is provided (will use fallback gradient if no wallpaper)
            document.documentElement.className = classNames(
                document.documentElement.className,
                {
                    [ClassName.CUSTOM_BACKGROUND_IMG]: !!userSettingId,
                }
            );

            // Wallpaper (background image)
            if (userSettingId && wallpaperExtension) {
                const customWallpaperUrl = getCustomBrandingResourceCdnPath(
                    CustomBrandingCdnResource.WALLPAPER,
                    wallpaperExtension
                );
                if (await checkImageUrlExists(customWallpaperUrl)) {
                    document.documentElement.style.setProperty(
                        "--custom-branding-bg-img",
                        `url(${customWallpaperUrl})`
                    );
                    log.publishMetricWithDimensions(
                        METRIC_NAME.CUSTOM_BRANDING_ASSET_SUCCESS,
                        {
                            resource: CustomBrandingCdnResource.WALLPAPER,
                        }
                    );
                } else {
                    log.publishMetricWithDimensions(
                        METRIC_NAME.MISSING_CUSTOM_BRANDING_ASSET,
                        {
                            resource: CustomBrandingCdnResource.WALLPAPER,
                        }
                    );
                }
            }

            // Favicon
            const faviconElement: HTMLLinkElement | null = document.querySelector(
                'link[rel="icon"]'
            );
            if (faviconElement) {
                if (!userSettingId || !faviconExtension) {
                    faviconElement.href = wsbFavicon;
                } else {
                    const faviconUrl = getCustomBrandingResourceCdnPath(
                        CustomBrandingCdnResource.FAVICON,
                        faviconExtension
                    );
                    if (await checkImageUrlExists(faviconUrl)) {
                        faviconElement.href = faviconUrl;
                        log.publishMetricWithDimensions(
                            METRIC_NAME.CUSTOM_BRANDING_ASSET_SUCCESS,
                            {
                                resource: CustomBrandingCdnResource.FAVICON,
                            }
                        );
                    } else {
                        log.publishMetricWithDimensions(
                            METRIC_NAME.MISSING_CUSTOM_BRANDING_ASSET,
                            {
                                resource: CustomBrandingCdnResource.FAVICON,
                            }
                        );
                    }
                }
            }

            // Font theme
            const isLightTheme = colorTheme === ColorTheme.LIGHT;
            const isDarkTheme = colorTheme === ColorTheme.DARK;

            document.documentElement.className = classNames(
                document.documentElement.className,
                {
                    [ClassName.CUSTOM_LIGHT_FONT_THEME]: isLightTheme,
                    [ClassName.CUSTOM_DARK_FONT_THEME]: isDarkTheme,
                }
            );
            if (isLightTheme) {
                applyMode(Mode.Light);
                setCustomFontBasedCloudscapeTheme(Mode.Light);
            } else if (isDarkTheme) {
                applyMode(Mode.Dark);
                setCustomFontBasedCloudscapeTheme(Mode.Dark);
            }

            // Logo
            if (!userSettingId || !logoExtension) {
                // Will set WSB logo based on end users' device type
                setBrandLogoPath("");
            } else {
                const logoPath = getCustomBrandingResourceCdnPath(
                    CustomBrandingCdnResource.LOGO,
                    logoExtension
                );
                if (await checkImageUrlExists(logoPath)) {
                    setBrandLogoPath(logoPath);
                    log.publishMetricWithDimensions(
                        METRIC_NAME.CUSTOM_BRANDING_ASSET_SUCCESS,
                        {
                            resource: CustomBrandingCdnResource.LOGO,
                        }
                    );
                } else {
                    setBrandLogoPath(EMPTY_LOGO_GIF);
                    log.publishMetricWithDimensions(
                        METRIC_NAME.MISSING_CUSTOM_BRANDING_ASSET,
                        {
                            resource: CustomBrandingCdnResource.LOGO,
                        }
                    );
                }
            }

            setHasTermsOfService(isTermsOfServiceProvided ?? false);

            setIsResourceReady(true);
        };
        initializeCustomBrandingAsset();
    }, [customBrandingConfig?.userSettingId]);
};
